<template>
  <v-row>
    <v-col v-if="treatment">
      <v-row dense>
        <v-col>
          <v-combobox
            label="Tratamentos"
            v-model="treatmentTemplate"
            dense
            hide-details
            outlined
            :items="treatmentTemplates"
            item-text="name"
            background-color="white"
            @change="debouncedSaveTreatment"
            :disabled="hasMedicalRequest || disabled"
          />
        </v-col>

        <v-col cols="4">
          <v-text-field
            label="Data de execução"
            v-model="executedAt"
            dense
            hide-details
            outlined
            background-color="white"
            @change="debouncedSaveTreatment"
            type="datetime-local"
            :disabled="disabled"
          />
        </v-col>
      </v-row>

      <v-row dense>
        <v-col>
          <v-combobox
            label="Solicitante"
            v-model="requester"
            dense
            hide-details
            outlined
            :items="doctorUsers"
            item-text="employee.name"
            background-color="white"
            @change="debouncedSaveTreatment"
            :disabled="hasMedicalRequest || disabled"
          />
        </v-col>

        <v-col>
          <v-combobox
            label="Encaminhar para Executante"
            v-model="performer"
            dense
            hide-details
            outlined
            :items="doctorUsers"
            item-text="employee.name"
            background-color="white"
            @change="debouncedSaveTreatment"
            :disabled="disabled"
          />
        </v-col>

        <v-col>
          <v-combobox
            label="Auxiliares"
            v-model="assistants"
            dense
            hide-details
            outlined
            :items="nurseUsers"
            item-text="employee.name"
            multiple
            chips
            deletable-chips
            small-chips
            background-color="white"
            @change="debouncedSaveTreatment"
            :disabled="disabled"
          />
        </v-col>
      </v-row>

      <DynamicFormCore
        v-if="dynamicFormType"
        :dynamicFormType="dynamicFormType"
        :answerableId="treatment.id"
        answerableType="Treatment"
        :disabled="disabled"
      />

      <v-row dense>
        <v-col>
          <v-row dense>
            <v-col class="text-h5 gray--text"> Termos de Consentimento </v-col>
          </v-row>

          <v-row dense>
            <v-col>
              <FileUpload
                ownerType="Treatment::ConsementTerm"
                :ownerId="treatment.consementTerm.id"
                v-if="treatment"
                :disabled="disabled"
              />
            </v-col>
          </v-row>
        </v-col>

        <v-col>
          <v-row dense>
            <v-col class="text-h5 gray--text"> Anexos </v-col>
          </v-row>

          <v-row dense>
            <v-col>
              <FileUpload
                ownerType="Treatment::Attachment"
                :ownerId="treatment.attachment.id"
                v-if="treatment"
                :disabled="disabled"
              />
            </v-col>
          </v-row>
        </v-col>
      </v-row>
    </v-col>
  </v-row>
</template>

<script>
import DynamicFormCore from "@/components/dynamic_form/core.vue";

export default {
  name: "EditableTreatment",
  props: {
    attendance: Object,
    treatment: Object,
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    DynamicFormCore,
  },
  data() {
    return {
      treatmentTemplates: [],
      users: [],
      treatmentTemplate: null,
      executedAt: "",
      requester: "",
      performer: "",
      assistants: [],
    };
  },
  created: function () {
    this.debouncedSaveTreatment = this.lodash.debounce(
      this.saveTreatment,
      2000
    );
  },
  mounted() {
    this.executedAt = this.$moment(new Date(this.treatment.executedAt)).format(
      "YYYY-MM-DDTHH:mm"
    );

    this.loadTreatmentTemplate();
    this.loadUsers();

    if (!this.treatment.executedAt) {
      this.executedAt = this.$moment(new Date()).format("YYYY-MM-DDTHH:mm");
      this.debouncedSaveTreatment();
    }
  },
  apollo: {
    treatmentTemplates: {
      query: require("@/graphql/treatment/treatment_templates.gql"),
      update(data) {
        let treatments = this.lodash.orderBy(
          data.treatmentTemplates,
          [
            (e) =>
              e.name
                .toLowerCase()
                .normalize("NFD")
                .replace(/[\u0300-\u036f]/g, ""),
          ],
          ["asc"]
        );
        treatments = this.lodash.filter(treatments, { visible: true });
        return treatments;
      },
      result() {
        this.loadTreatmentTemplate();
      },
    },
    users: {
      query: require("@/graphql/users.gql"),
      result() {
        this.loadUsers();
      },
    },
  },
  computed: {
    dynamicFormType() {
      if (this.treatmentTemplate?.dynamicForm?.dynamicFormType) {
        return this.treatmentTemplate.dynamicForm.dynamicFormType;
      }

      return null;
    },
    doctorUsers() {
      return this.lodash.orderBy(
        this.lodash.filter(this.users, {
          employee: { employeeType: "doctor" },
        }),
        [
          (e) =>
            e.employee.name
              .toLowerCase()
              .normalize("NFD")
              .replace(/[\u0300-\u036f]/g, ""),
        ],
        ["asc"]
      );
    },
    nurseUsers() {
      return this.lodash.orderBy(
        this.lodash.filter(this.users, {
          employee: { employeeType: "nurse" },
        }),
        [
          (e) =>
            e.employee.name
              .toLowerCase()
              .normalize("NFD")
              .replace(/[\u0300-\u036f]/g, ""),
        ],
        ["asc"]
      );
    },
    hasMedicalRequest() {
      if (this.treatment?.medicalRequest?.id) {
        return true;
      }

      return false;
    },
  },
  methods: {
    loadTreatmentTemplate() {
      if (this.treatment?.treatmentTemplate?.id) {
        this.treatmentTemplate = this.lodash.find(this.treatmentTemplates, {
          id: this.treatment.treatmentTemplate.id,
        });
      }
    },
    loadUsers() {
      if (this.treatment?.requester?.id) {
        this.requester = this.lodash.find(this.doctorUsers, {
          id: this.treatment.requester.id,
        });
      }

      if (this.treatment?.performer?.id) {
        this.performer = this.lodash.find(this.doctorUsers, {
          id: this.treatment.performer.id,
        });
      }

      if (this.treatment) {
        this.assistants = this.lodash.filter(
          this.nurseUsers,
          function (e) {
            return this.lodash
              .map(this.treatment.assistants, "id")
              .includes(e.id);
          }.bind(this)
        );
      }
    },
    saveTreatment() {
      if (this.disabled) return;

      return this.apolloMutate({
        mutation: require("@/graphql/treatment/save_treatment.gql"),
        variables: {
          attendanceId: this.attendance.id,
          treatmentId: this.lodash.get(this.treatment, "id", ""),
          treatmentTemplateId: this.lodash.get(
            this.treatmentTemplate,
            "id",
            ""
          ),
          executedAt: this.executedAt,
          requesterId: this.lodash.get(this.requester, "id", ""),
          performerId: this.lodash.get(this.performer, "id", ""),
          assistantIds: this.lodash.map(this.assistants, "id"),
        },
      }).then(({ data }) => {
        return data.saveTreatment;
      });
    },
  },
};
</script>

<style lang="scss" scoped></style>
